// @ts-ignore
/* eslint-disable */
import { request } from "@umijs/max";

/** 字典项列表（分页） 查询字典项列表（分页、搜索、排序）

支持分页、搜索、过滤和排序，适合运营后台的列表展示。符合 Ant Design Pro 规范。

## 参数说明

- **current**: 当前页码，从1开始（默认1）
- **pageSize**: 每页数量，最大100（默认10）
- **sorter**: 排序参数（JSON字符串，如 {"field":"created_at","order":"descend"}）
- **type**: 字典类型过滤（可选，如 job_position/industry/jd_complaint_type/jd_review_reject_reason）
- **parent_id**: 父级ID过滤（可选，用于查询层级结构，如查询某个城市下的所有街道）
- **is_active**: 是否只返回启用的项（可选，默认返回所有）

## 使用示例

### 分页查询
```
GET /dictionaries?current=1&pageSize=20&type=job_position
```

### 过滤查询
```
GET /dictionaries?type=job_position&parent_id=parent_id
```

### 排序
```
GET /dictionaries?sorter={"field":"created_at","order":"descend"}
``` GET /api/admin/dictionaries */
export async function listDictionariesApiAdminDictionariesGet(
  // 叠加生成的Param类型 (非body参数swagger默认没有生成对象)
  params: API.listDictionariesApiAdminDictionariesGetParams,
  options?: { [key: string]: any }
) {
  return request<API.PaginatedResponseDictionaryResponse_>(
    "/api/admin/dictionaries",
    {
      method: "GET",
      params: {
        // current has a default value: 1
        current: "1",
        // pageSize has a default value: 10
        pageSize: "10",
        ...params,
      },
      ...(options || {}),
    }
  );
}

/** 创建字典项 创建字典项

用于运营后台创建新的字典项（如岗位、薪资范围等）。

## 字典类型说明（type）

支持以下字典类型，每个类型都有明确的业务含义：

- **job_position**: 岗位标签列表，用于用户选择期望的职位类型
  - 示例：软件工程师、销售经理、产品经理
  - JD 的 job_position_tags 存储此类型的字典项 code（即 id）
  - 通常为单层结构（无 parent_id）

- **jd_complaint_type**: 岗位投诉类型
  - C 端用户对岗位进行投诉时的投诉类型选择（如：虚假信息、职位已关闭等）
  - 暴露给 C 端使用，通常为单层结构（无 parent_id）

- **ad_channel_facebook**: Facebook 投放渠道配置
  - 用于管理 Facebook 广告投放渠道及其入口层级
  - 支持多层级结构（渠道 → Campaign → AdSet → Ad）

- **ad_channel_tiktok**: TikTok 投放渠道配置
  - 用于管理 TikTok 广告投放渠道及其入口层级
  - 支持多层级结构

- **ad_channel_google**: Google 投放渠道配置
  - 用于管理 Google 广告投放渠道及其入口层级
  - 支持多层级结构

- **company_size**: 企业规模配置
  - 用于管理企业规模分类（如：1-50人、50-200人、200-500人等）
  - 仅后台管理使用，不暴露给 C 端
  - 通常为单层结构（无 parent_id）

- **industry**: 行业配置
  - 用于管理行业分类（如：互联网、制造业、博彩、服务业等）
  - JD 的 industry_code 存储此类型的字典项 code（即 id）
  - 暴露给 C 端使用
  - 通常为单层结构（无 parent_id）

- **jd_review_reject_reason**: 岗位审核不通过原因
  - 运营审核不通过时可快速选择主管配置的预设原因，仅后台使用
  - jd_review_records.rejection_reason_code 存储此类型的字典项 code（即 id）
  - 通常为单层结构（无 parent_id）

## 业务规则

- **value 字段**：
  - value 是字典项的真实含义/字面量（必填，建议统一用中文填写）
  - 用于展示，代表字典项的真实含义
- **display_text 字段**：
  - display_text 是多语言显示文本（可选，可以为空）
  - 如果不提供则使用空字典
  - 内容上应与 value 一致，主要用于多语言场景
  - 提示：可以使用 `/preview-display-text` 接口预览自动生成的 display_text，确认是否采用
- **层级关系**：
  - 如果指定 parent_id，父级必须存在且未删除
  - 父级的 type 必须与当前 type 相同（不能跨类型建立层级关系）
- **自动排序**：
  - 如果 sort_order 为 0，系统会自动计算排序值
  - 自动计算规则：同类型、同父级下的最大 sort_order + 1
  - 如果指定了非 0 的 sort_order，则使用指定值

## 参数说明

- **type**: 字典类型（必填，枚举值：job_position/jd_complaint_type/jd_review_reject_reason（岗位审核不通过原因，仅后台）/ad_channel_facebook/ad_channel_tiktok/ad_channel_google/company_size/industry）
- **value**: 字典项的真实含义/字面量（必填，建议统一用中文填写）
- **display_text**: 多语言显示文本（可选，可以为空，如果不提供则使用空字典）
- **parent_id**: 父级ID（可选，传 `null` 表示根节点。用于层级结构，如街道关联城市）
- **sort_order**: 排序顺序（默认 0，会自动计算；数字越小越靠前）
- **is_active**: 是否启用（默认 True）
- **extra**: 扩展字段（可选，JSONB）。若需携带资源（如行业标签配图），见下方「extra.assets 资源约定」。

## extra.assets 资源约定（各端对齐）

当字典项需要携带资源（如图片）时，在 extra 中使用固定字段 **assets**（数组）：

- **extra.assets**：数组，每项为一条资源。
- 每项结构：
  - **type**：资源类型，目前仅支持 `"image"`。
  - **url_2x**：2x 图 URL（**必填**）。至少需配置 2x，以保障清晰度。
  - **url_1x**：1x 图 URL（可选）。可与 url_2x 同时存在；图片尺寸与 1x/2x 的具体配置由运营后台前端处理。
- 图片尺寸、裁剪及实际 1x/2x 的选用由运营后台前端负责；后端只约定数据结构与字段含义。

示例（如行业标签配图）：
```json
"extra": {
  "assets": [
    { "type": "image", "url_2x": "https://...", "url_1x": "https://..." }
  ]
}
```

## 使用示例

### 创建根节点（不提供 display_text，使用空字典）
```
POST /dictionaries
{
  "type": "job_position",
  "value": "软件工程师"
  // display_text 不提供，使用空字典 {}
}
```

### 创建根节点（使用预览接口生成 display_text）
```
// 1. 先预览自动生成的 display_text
POST /dictionaries/preview-display-text
{
  "value": "软件工程师"
}
// 返回建议的 display_text

// 2. 创建时使用预览的结果
POST /dictionaries
{
  "type": "job_position",
  "value": "软件工程师",
  "display_text": {
    "zh_CN": "软件工程师",
    "en_US": "软件工程师",
    "km_KH": "软件工程师"
  }
}
```

### 创建子节点（如层级类型）
```
POST /dictionaries
{
  "type": "industry",
  "value": "互联网",
  "parent_id": "parent_dictionary_id",
  "display_text": {
    "zh_CN": "互联网",
    "en_US": "Internet"
  }
}
``` POST /api/admin/dictionaries */
export async function createDictionaryEndpointApiAdminDictionariesPost(
  body: API.DictionaryCreateRequest,
  options?: { [key: string]: any }
) {
  return request<API.BaseResponseDictionaryResponse_>(
    "/api/admin/dictionaries",
    {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      data: body,
      ...(options || {}),
    }
  );
}

/** 字典项详情 获取字典项详情

根据字典项ID查询详细信息。 GET /api/admin/dictionaries/${param0} */
export async function getDictionaryApiAdminDictionariesDictionaryIdGet(
  // 叠加生成的Param类型 (非body参数swagger默认没有生成对象)
  params: API.getDictionaryApiAdminDictionariesDictionaryIdGetParams,
  options?: { [key: string]: any }
) {
  const { dictionary_id: param0, ...queryParams } = params;
  return request<API.BaseResponseDictionaryResponse_>(
    `/api/admin/dictionaries/${param0}`,
    {
      method: "GET",
      params: { ...queryParams },
      ...(options || {}),
    }
  );
}

/** 更新字典项 更新字典项

支持增量更新，可以只提供需要修改的字段。

## 业务规则

- 所有字段都是可选的
- 如果更新 display_text，必须包含至少一个语言
- 如果更新 parent_id，父级必须存在（设置为 null 可移除父级关联）

## 参数说明

- **value**: 字典项的真实含义/字面量（可选）
- **display_text**: 多语言显示文本（可选）
- **parent_id**: 父级ID（可选，设置为 null 可移除父级关联）
- **sort_order**: 排序顺序（可选）
- **is_active**: 是否启用（可选）
- **extra**: 扩展字段（可选）。若含资源，使用固定字段 assets（数组），每项 type/url_2x/url_1x，详见「创建字典项」接口文档中的「extra.assets 资源约定」。 PUT /api/admin/dictionaries/${param0} */
export async function updateDictionaryEndpointApiAdminDictionariesDictionaryIdPut(
  // 叠加生成的Param类型 (非body参数swagger默认没有生成对象)
  params: API.updateDictionaryEndpointApiAdminDictionariesDictionaryIdPutParams,
  body: API.DictionaryUpdateRequest,
  options?: { [key: string]: any }
) {
  const { dictionary_id: param0, ...queryParams } = params;
  return request<API.BaseResponseDictionaryResponse_>(
    `/api/admin/dictionaries/${param0}`,
    {
      method: "PUT",
      headers: {
        "Content-Type": "application/json",
      },
      params: { ...queryParams },
      data: body,
      ...(options || {}),
    }
  );
}

/** 删除字典项（逻辑删除） 删除字典项（逻辑删除）

执行逻辑删除，不会物理删除数据。 DELETE /api/admin/dictionaries/${param0} */
export async function deleteDictionaryEndpointApiAdminDictionariesDictionaryIdDelete(
  // 叠加生成的Param类型 (非body参数swagger默认没有生成对象)
  params: API.deleteDictionaryEndpointApiAdminDictionariesDictionaryIdDeleteParams,
  options?: { [key: string]: any }
) {
  const { dictionary_id: param0, ...queryParams } = params;
  return request<API.BaseResponseDictStr_str_>(
    `/api/admin/dictionaries/${param0}`,
    {
      method: "DELETE",
      params: { ...queryParams },
      ...(options || {}),
    }
  );
}

/** 移动字典项（改变父级） 移动字典项（改变父级）

用于调整字典项的层级关系，如将街道从一个城市移动到另一个城市。

## 参数说明

- **parent_id**: 新的父级ID（设置为 null 可移动到根节点）

## 业务规则

- 不能将字典项设置为自己的父级
- 不能将字典项移动到其子节点下（防止循环引用）
- 如果指定 parent_id，父级必须存在

## 使用场景

- 调整层级结构（如城市-街道关系）
- 将子节点提升为根节点
- 将根节点移动到某个父级下 POST /api/admin/dictionaries/${param0}/move */
export async function moveDictionaryEndpointApiAdminDictionariesDictionaryIdMovePost(
  // 叠加生成的Param类型 (非body参数swagger默认没有生成对象)
  params: API.moveDictionaryEndpointApiAdminDictionariesDictionaryIdMovePostParams,
  body: API.DictionaryMoveRequest,
  options?: { [key: string]: any }
) {
  const { dictionary_id: param0, ...queryParams } = params;
  return request<API.BaseResponseDictionaryResponse_>(
    `/api/admin/dictionaries/${param0}/move`,
    {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      params: { ...queryParams },
      data: body,
      ...(options || {}),
    }
  );
}

/** 获取字典选项列表 获取字典选项列表（用于 ProTable/ProForm 的 valueEnum）

返回格式符合 ProComponents 规范，前端可直接转换为 valueEnum。

## 参数说明

- **type**: 字典类型（必填，如 job_position/industry/jd_complaint_type/jd_review_reject_reason）
- **is_active**: 是否只返回启用的项（默认 true）

## 使用场景

适用于前端表单选择、表格筛选等场景：
- ProTable 的列筛选（valueEnum）
- ProForm 的下拉选择（Select/Radio/Checkbox）
- 搜索表单的筛选条件

## 前端使用示例

```typescript
// 获取字典选项
const fetchStatusEnum = async () => {
    const response = await request('/api/admin/dictionaries/user_status/options');
    
    const valueEnum: Record<string, any> = {};
    response.data?.forEach((item: any) => {
        valueEnum[item.value] = {
            text: item.label,
            status: item.status,
            disabled: item.disabled,
        };
    });
    
    return valueEnum;
};

// 在 ProTable 中使用
const columns = [
    {
        title: '状态',
        dataIndex: 'status',
        request: fetchStatusEnum,  // ProTable 会自动调用
    },
];
```

## 注意事项

- 此接口不需要管理员认证，可供前端公开调用
- 默认只返回启用的字典项（is_active=true）
- 返回的选项按 sort_order 升序排列 GET /api/admin/dictionaries/${param0}/options */
export async function getDictionaryOptionsApiAdminDictionariesTypeOptionsGet(
  // 叠加生成的Param类型 (非body参数swagger默认没有生成对象)
  params: API.getDictionaryOptionsApiAdminDictionariesTypeOptionsGetParams,
  options?: { [key: string]: any }
) {
  const { type: param0, ...queryParams } = params;
  return request<API.BaseResponseListDictionaryOptionResponse_>(
    `/api/admin/dictionaries/${param0}/options`,
    {
      method: "GET",
      params: {
        // is_active has a default value: true
        is_active: "true",
        ...queryParams,
      },
      ...(options || {}),
    }
  );
}

/** 岗位标签树（含岗位数） 查询岗位标签树，每个节点带岗位数（jd_count）。

叶子节点：jd_count = 该标签 code 在岗位中出现的岗位数。
父节点：jd_count = 其下所有叶子节点的岗位数之和（父节点本身不存于 JD，仅汇总展示）。

前端可直接用返回的树结构匹配展示，无需再算计数。 GET /api/admin/dictionaries/job_position/tree-with-count */
export async function listJobPositionTreeWithCountApiAdminDictionariesJobPositionTreeWithCountGet(
  // 叠加生成的Param类型 (非body参数swagger默认没有生成对象)
  params: API.listJobPositionTreeWithCountApiAdminDictionariesJobPositionTreeWithCountGetParams,
  options?: { [key: string]: any }
) {
  return request<API.PaginatedResponseDictionaryTreeWithCountResponse_>(
    "/api/admin/dictionaries/job_position/tree-with-count",
    {
      method: "GET",
      params: {
        ...params,
      },
      ...(options || {}),
    }
  );
}

/** 预览字典显示文本 预览根据 value 自动生成的 display_text（AI 翻译）

使用 LLM 自动将字典项的 value 翻译成多语言 display_text（支持中英柬三种语言）。
在创建字典项之前，可以先预览自动生成的 display_text，确认是否合适。
如果自动生成的 display_text 不够好，可以手动指定更合适的 display_text。

## 使用场景

- 运营人员在创建字典项时，可以先预览自动生成的 display_text，确认是否合适
- 如果自动生成的 display_text 不够好，可以手动指定更合适的 display_text
- 可以指定目标语言列表，预览不同语言的生成结果

## 参数说明

- **value**: 字典项的真实含义/字面量（必填，用于生成预览）
- **target_languages**: 目标语言列表（可选，默认：["zh_CN", "en_US", "km_KH"]）
  - 支持的语言：zh_CN（中文简体）、en_US（英文）、km_KH（高棉语）

## 返回说明

- **suggested_display_text**: AI 自动生成的多语言显示文本

## 使用示例

### 预览 display_text（使用默认语言）
```
POST /dictionaries/preview-display-text
{
  "value": "软件工程师"
}
```
返回：
```json
{
  "code": 0,
  "message": "success",
  "data": {
    "suggested_display_text": {
      "zh_CN": "软件工程师",
      "en_US": "Software Engineer",
      "km_KH": "វិស្វករសូហ្វវែរ"
    }
  },
  "trace_id": "..."
}
```

### 预览 display_text（指定语言）
```
POST /dictionaries/preview-display-text
{
  "value": "软件工程师",
  "target_languages": ["zh_CN", "en_US"]
}
```
返回：
```json
{
  "code": 0,
  "message": "success",
  "data": {
    "suggested_display_text": {
      "zh_CN": "软件工程师",
      "en_US": "Software Engineer"
    }
  },
  "trace_id": "..."
}
```

## 注意事项

- 使用 LLM 自动翻译，翻译质量通常较高，但仍建议人工审核
- 如果 LLM 调用失败或返回无效结果，将降级使用原文作为所有语言的翻译
- 如果预览结果不满意，可以在创建时手动指定 display_text POST /api/admin/dictionaries/preview-display-text */
export async function previewDisplayTextEndpointApiAdminDictionariesPreviewDisplayTextPost(
  body: API.DictionaryDisplayTextPreviewRequest,
  options?: { [key: string]: any }
) {
  return request<API.BaseResponseDictionaryDisplayTextPreviewResponse_>(
    "/api/admin/dictionaries/preview-display-text",
    {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      data: body,
      ...(options || {}),
    }
  );
}

/** 字典项树形结构列表 查询字典项树形结构列表

返回树形结构，方便运营后台进行层级管理。所有配置都以树形结构返回，即使没有层级关系。
符合 Ant Design Pro 规范，使用分页响应格式。

## 参数说明

- **type**: 字典类型过滤（可选，如 job_position/industry/jd_complaint_type/jd_review_reject_reason）
- **is_active**: 是否只返回启用的项（可选，默认返回所有）

## 返回说明

返回分页格式的树形结构，data.list 中包含根节点列表，每个节点包含：
- 字典项的所有字段
- **children**: 子节点列表（树形结构，如果为空则表示叶子节点）

## 使用场景

适用于需要层级管理的场景，如：
- 分类管理（一级分类-二级分类）
- 组织架构管理 GET /api/admin/dictionaries/tree */
export async function listDictionariesTreeApiAdminDictionariesTreeGet(
  // 叠加生成的Param类型 (非body参数swagger默认没有生成对象)
  params: API.listDictionariesTreeApiAdminDictionariesTreeGetParams,
  options?: { [key: string]: any }
) {
  return request<API.PaginatedResponseDictionaryTreeResponse_>(
    "/api/admin/dictionaries/tree",
    {
      method: "GET",
      params: {
        ...params,
      },
      ...(options || {}),
    }
  );
}
